/**
 * "This packet isn't ready to send yet" (#1569).
 *
 * ONE component, two screens: the job page's Resend dialog and Close Out's
 * action bar. They ask the same question of the same endpoint, so they must
 * say the same words — two surfaces drifting apart on one fact is the #832
 * bug class, and this one is about whether a customer gets their paperwork.
 *
 * What the two screens do NOT share is the consequence, which is why that is
 * the caller's to pass in:
 *
 *  - On a RESEND the button is dead (Corbin, 2026-09-21: "I don't want to be
 *    able to send one or queue one before it's been fully reviewed"). The
 *    first packet already went; parking a second one is how 17 Sep's silent
 *    no-op happened.
 *  - On the FIRST send in Close Out the button stays live, because parking
 *    there is the whole design (CLOSEOUT-PACKET-HOLD) — the approve also
 *    finalises the invoice and can charge the card, so blocking it would stop
 *    billing on any job with one unfiled device.
 */
import { AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import { deviceLabel, type PacketBlock } from "@/lib/use-packet-readiness";

export interface PacketNotReadyNoticeProps {
    block: PacketBlock;
    jobId: string;
    /** What happens if they press send anyway — the one thing the two callers
     *  genuinely differ on. */
    consequence: "blocked" | "will-park";
    /** A packet is ALREADY parked on this job, with who and when. */
    queued?: { at: string; by: string | null } | null;
    /** How to get to the fix. The job page already has a Paperwork card in
     *  reach, so it passes a callback into its own `openChapter("paperwork")`
     *  — scrolling beats a navigation that reloads the page you are on.
     *  Close Out is a different screen and gets the link instead. */
    onGoToPaperwork?: () => void;
}

function bodyFor(block: PacketBlock, consequence: "blocked" | "will-park"): string {
    if (block.kind === "work_order") {
        return consequence === "blocked"
            ? "The completed work order hasn't been attached, so the packet can't go out yet."
            : "The completed work order hasn't been attached. The packet will wait and go out as one email once it is.";
    }
    const short = block.total - block.filed;
    if (block.portal) {
        const noun = short === 1 ? "device is" : "devices are";
        const tail =
            consequence === "blocked"
                ? "so the packet can't go out yet."
                : "The packet will wait and go out as one email once they come back.";
        return `${short} of ${block.total} ${noun} still waiting on the official report from the city — a scan of our own paperwork doesn't clear it, ${tail}`;
    }
    const noun = short === 1 ? "device has" : "devices have";
    return consequence === "blocked"
        ? `${short} of ${block.total} ${noun} no test report attached, so the packet can't go out yet.`
        : `${short} of ${block.total} ${noun} no test report attached. The packet will wait and go out as one complete email the moment the last report lands.`;
}

export function PacketNotReadyNotice({
    block,
    jobId,
    consequence,
    queued,
    onGoToPaperwork,
}: PacketNotReadyNoticeProps) {
    const devices = block.kind === "reports" ? block.missing : [];
    // The count is exact; the list is capped server side. Saying "and N more"
    // is the difference between a trustworthy list and one that quietly lies
    // about a 40-device campus.
    const shortfall = block.kind === "reports" ? block.total - block.filed : 0;
    const undisclosed = Math.max(0, shortfall - devices.length);

    return (
        <div
            data-testid="packet-not-ready"
            className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-[12.5px] leading-relaxed text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100"
        >
            <div className="flex items-center gap-1.5 font-bold">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                {queued ? "A packet is already queued for this job" : "This packet isn't ready to send yet"}
            </div>
            <p className="mt-0.5">
                {queued ? (
                    <>
                        Queued {queued.at}
                        {queued.by ? ` by ${queued.by}` : ""}. Pressing again won&apos;t hurry it along and won&apos;t
                        send a second copy.
                    </>
                ) : (
                    bodyFor(block, consequence)
                )}
            </p>

            {devices.length > 0 && (
                <ul className="mt-2 flex flex-col gap-1">
                    {devices.map((d) => (
                        <li
                            key={d.assetId}
                            className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded-md border border-amber-300/70 bg-white/70 px-2 py-1 dark:border-amber-500/30 dark:bg-amber-950/20"
                        >
                            <span className="font-semibold">{deviceLabel(d)}</span>
                            {d.serialNumber && (
                                <span className="font-mono text-[11px] opacity-80">S/N {d.serialNumber}</span>
                            )}
                            {d.locationDescription && (
                                <span className="text-[11px] opacity-70">· {d.locationDescription}</span>
                            )}
                        </li>
                    ))}
                    {undisclosed > 0 && (
                        <li className="px-2 text-[11px] opacity-80">
                            …and {undisclosed} more {undisclosed === 1 ? "device" : "devices"}.
                        </li>
                    )}
                </ul>
            )}

            {/* Where to go and fix it. On a portal job Paperwork is the wrong
                answer — nobody in the office can attach their way out of a
                report the city hasn't returned — so that case points at the
                filing queue instead (Corbin chose Paperwork for the rest,
                2026-09-21). */}
            {block.kind === "reports" && block.portal ? (
                <Link
                    to="/submissions"
                    className="mt-2 inline-block border-b-[1.5px] border-amber-400 pb-px text-[12px] font-bold hover:border-amber-600"
                >
                    Open the filing queue →
                </Link>
            ) : onGoToPaperwork ? (
                <button
                    type="button"
                    onClick={onGoToPaperwork}
                    className="mt-2 inline-block border-b-[1.5px] border-amber-400 pb-px text-[12px] font-bold hover:border-amber-600"
                >
                    Go to Paperwork →
                </button>
            ) : (
                <Link
                    to={`/jobs/${jobId}`}
                    className="mt-2 inline-block border-b-[1.5px] border-amber-400 pb-px text-[12px] font-bold hover:border-amber-600"
                >
                    Go to Paperwork →
                </Link>
            )}
        </div>
    );
}
